"use client"

import { Fragment } from "react"
import { Button, Col, Row, Skeleton } from "antd"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faX } from "@fortawesome/free-solid-svg-icons"
import SelectPeopleSkeleton from "./SelectPeople.skeleton"
import SelectCabinClassSkeleton from "./SelectCabinClass.skeleton"
import ButtonSubmitSearchFlightsSkeleton from "./ButtonSubmitSearchFlights.skeleton"
import SelectAirportFromSkeleton from "./SelectAirportFrom.skeleton"
import SelectDateFromToSkeleton from "./SelectDateFromTo.skeleton"

type SearchingFlightsSkeletonProps = {
  layout?: "default" | "hero"
}

const SearchingFlightsSkeleton = ({ layout = "default" }: SearchingFlightsSkeletonProps) => {
  const inputFlights = useAppSelector(state => state.flight.inputFlights)
  const dispatch = useAppDispatch()

  const isMultiCities = useAppSelector(state => state.flight.isMultiCities)
  const isMax = inputFlights.length >= 3
  const isHero = layout === "hero"

  if (isHero && !isMultiCities) {
    return (
      <div className="home__searching--hero-form">
        <Row className="home__searching--hero-row" gutter={[14, 12]} wrap={false}>
          <Col flex="2.1 1 0" className="home__searching--hero-airports-col">
            <Row className="home__searching--hero-airports" gutter={14} wrap={false}>
              <Col flex="1 1 0"><Skeleton.Input active block style={{ height: "4.7rem" }} /></Col>
              <Col flex="none"><Skeleton.Button active style={{ width: "2.8rem", minWidth: "2.8rem", height: "2.8rem" }} /></Col>
              <Col flex="1 1 0"><Skeleton.Input active block style={{ height: "4.7rem" }} /></Col>
            </Row>
          </Col>
          <Col flex="1.35 1 0">
            <Skeleton.Input active block style={{ height: "1.4rem", marginBottom: "0.45rem", maxWidth: "12rem" }} />
            <Skeleton.Input active block style={{ height: "4.7rem", borderRadius: "15px" }} />
          </Col>
        </Row>
        <Row
          className="home__searching--hero-row home__searching--hero-row--secondary home__searching--hero-form-row-2"
          gutter={[14, 12]}
          wrap={false}
        >
          <Col flex="2.1 1 0" className="home__searching--hero-airports-col">
            <Row className="home__searching--hero-airports" gutter={14} wrap={false}>
              <Col flex="1 1 0"><Skeleton.Input active block style={{ height: "4.1rem" }} /></Col>
              <Col flex="none" className="home__searching--hero-swap-col home__searching--hero-swap-col--spacer" aria-hidden>
                <span className="home__searching--hero-swap-spacer" />
              </Col>
              <Col flex="1 1 0"><Skeleton.Input active block style={{ height: "4.1rem" }} /></Col>
            </Row>
          </Col>
          <Col flex="1.35 1 0"><Skeleton.Button active block style={{ height: "4.1rem" }} /></Col>
        </Row>
      </div>
    )
  }

  return (

    <Fragment>
      <Row justify='space-between' align='middle' style={!isMultiCities ? {} : { marginBottom: '1rem' }}>
        <Col>
          <Row gutter={10} wrap={false} style={{ marginBottom: '1rem' }}>
            <Col>
              <Skeleton
                paragraph={false}
                styles={{
                  title: {
                    margin: 0,
                    height: '2.5rem',
                    width: '20rem'
                  }
                }}
              />
            </Col>
            <Col>
              <Skeleton
                paragraph={false}
                styles={{
                  title: {
                    margin: 0,
                    height: '2.5rem',
                    width: '20rem'
                  }
                }}
              />
            </Col>
          </Row>
        </Col>
        <Col>
          {isMultiCities && (
            <Row align='middle' gutter={[15, 15]}>
              <Col>
                <SelectPeopleSkeleton />
              </Col>
              <Col>
                <SelectCabinClassSkeleton />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      {inputFlights.map((input, index) => {
        if (input.isReturn) {
          return <></>
        }
        return (
          <Row key={index} gutter={[15, 15]} style={index < (inputFlights.length - 1) ? { marginBottom: '1rem' } : {}}>
            <Col span={isMultiCities ? 16 : 12}>
              <Row wrap={false} style={{ height: '100%' }}>
                <Col flex={1}>
                  <Row>
                    <Col flex={isMultiCities ? 1 : undefined} span={isMultiCities ? undefined : 24}>
                      <SelectAirportFromSkeleton />
                    </Col>
                  </Row>
                  {!isMultiCities && (
                    <Row>
                      <SelectPeopleSkeleton />
                    </Row>
                  )}
                </Col>
                <Col style={{ paddingInline: 5 }}>
                  <Row align='middle' style={{ height: `calc(100% - ${isMultiCities ? 0 : 55}px)` }}>
                    <Skeleton.Button
                      active
                      styles={{
                        content: {
                          height: '3.2rem',
                          width: '3.2rem',
                          minWidth: '3.2rem'
                        }
                      }}
                      
                    />
                  </Row>
                  {!isMultiCities && (<Row style={{ height: 55 }}></Row>)}
                </Col>
                <Col flex={1}>
                  <Row>
                    <SelectAirportFromSkeleton />
                  </Row>
                  {!isMultiCities && (
                    <Row>
                      <Col span={24}>
                        <SelectCabinClassSkeleton />
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            </Col>
            <Col span={isMultiCities ? 8 : 12}>
              <Row gutter={[15, 15]}>
                <Col flex={isMultiCities ? 1 : undefined} span={isMultiCities ? undefined : 24}>
                  <SelectDateFromToSkeleton />
                </Col>
                {isMax && (
                  <Col>
                    <Row align='middle' style={{ height: '100%' }}>
                      <Button
                        onClick={() => dispatch(flightActions.removeFlightInput({ flightInputIndex: index }))}
                        type='primary'
                        ghost
                        danger
                        shape='circle'
                        size='large'
                        icon={<FontAwesomeIcon icon={faX} />}
                      />
                    </Row>
                  </Col>
                )}
              </Row>
              {!isMultiCities && (
                <Row style={{ marginTop: 15 }}>
                  <ButtonSubmitSearchFlightsSkeleton />
                </Row>
              )}
            </Col>
          </Row>
        )
      })}
      {isMultiCities && (
        <Row align='middle' justify='space-between' style={{ marginTop: '1rem' }}>
          <Col>
            {!isMax && (
              <Skeleton.Button
                size='large'
                active
                style={{ width: '20rem' }}
              />
            )}
          </Col>
          <Col>
            <ButtonSubmitSearchFlightsSkeleton />
          </Col>
        </Row>
      )}
    </Fragment>
  )
}

export default SearchingFlightsSkeleton